import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { AuthBackground } from '@/components/auth/AuthBackground';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Roles } from '@/types/auth/auth';

export default function ConsumerSignIn() {
  const navigate = useNavigate();
  const { signIn, user, userProfile, loading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await signIn(email, password);
    } catch (err) {
      setError("Invalid email or password");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;

    if (user && userProfile) {
      let targetPath = "/consumer";  // Default to separate consumer experience

      if (userProfile.role === Roles.Business || userProfile.role === Roles.Admin) {
        targetPath = "/dashboard";
      }

      navigate(targetPath, { replace: true });
    }
  }, [user, userProfile, authLoading, navigate]);

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero image background */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden">
        <AuthBackground />

        {/* Content */}
        <div className="relative z-10 max-w-lg text-white p-12">
          <div className="mb-12">
            <h1 className="text-5xl font-bold mb-6 leading-tight">Welcome Back</h1>
            <p className="text-xl text-white/90 leading-relaxed">Your genies are waiting to help you</p>
          </div>

          <div className="space-y-8">
            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Instant Help</h3>
                <p className="text-white/90 leading-relaxed">Get answers and assistance whenever you need them</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Your Favorites</h3>
                <p className="text-white/90 leading-relaxed">Access your saved genies and pick up where you left off</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">New Discoveries</h3>
                <p className="text-white/90 leading-relaxed">Find new genies to help with different aspects of your life</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Sign in form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-[#222B45]">Sign In</h2>
            <p className="mt-2 text-[#6B7280]">Access your personal genies</p>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end text-sm mt-2">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="font-medium text-genie-primary hover:text-[#3579cc]"
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            {error && <div className="text-sm text-red-500 text-center">{error}</div>}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
            >
              {loading ? "Signing In..." : "Sign In"}
            </Button>

            <div className="text-center text-sm">
              <span className="text-[#6B7280]">Don't have an account? </span>
              <button type="button" onClick={() => navigate("/signup")} className="text-genie-primary hover:text-[#3579cc] font-medium">
                Sign up
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
